import { ReactNode } from "react";
import { AutomotiveEffect } from "../pages/Playground/Automotive/Config/Effects/automotive-effects-store";
import { AutomotivePreset } from "../pages/Playground/Automotive/Config/Harmonization/automotive-harmonization-store";
import { BrandConfigType } from "../pages/Playground/ImageToImage/Config/Features/Brand/BrandConfig";
import { StyleConfigType } from "../pages/Playground/ImageToImage/Config/Features/Style/StyleConfig";
import { PlaygroundImage } from "./playground";
// TODO: Remove the re-export and update the imports where these types are used
export type { PlaygroundImage, PlaygroundResult, imageDimensions } from "./playground";

export type ImageToImageConfigType = {
	original_image: PlaygroundImage;
	style?: StyleConfigType;
	background?: BackgroundConfigType;
	brand?: BrandConfigType;
	size?: SizeConfigType;
	num_results: number;
	foreground_placement?: ForegroundPlacementType;
	object_manipulation?: ObjectManipulationType;
	manual_placement?: ManualPlacementConfigType;
	automotive?: AutomotiveConfigType;
};

export enum ImageEditingViewMode {
	SINGLE = "single",
	GALLERY = "gallery",
}

export enum ImageEditingResetBehavior {
	RESET = "reset",
	CLEAR = "clear",
}

export type ITIStyleProps = {
	url: string;
	face_seed?: number;
	face_sd_edit?: number;
	face_mask_steps?: number;
	num_inference_steps?: number;
	is_normalize_face?: boolean;
	canny_scale?: number;
	body_seed?: number;
	body_sd_edit?: number;
	logo_enhance?: boolean;
	box_threshold?: number;
	text_threshold?: number;
	max_size_height?: number;
	max_size_width?: number;
	min_size?: number;
};

export type AutomotiveProps = {
	effects?: AutomotiveEffectsProps;
	harmonization?: AutomotiveHarmonizationProps;
};

export type AutomotiveConfigType = {
	effects: EffectsConfigType;
	harmonization: HarmonizationConfigType;
};

export type EffectsConfigType = {
	image_file?: File;
	effect: AutomotiveEffect;
	layers?: boolean;
	seed?: string;
};
export type AutomotiveEffectsProps = {
	url: string;
	layer_url?: any;
	seed?: number;
};

export type HarmonizationConfigType = {
	image_file?: File;
	preset: AutomotivePreset;
};

export type AutomotiveHarmonizationProps = {
	url: string;
};

export type ITIBgProps = {
	remove?: RemoveBgRes;
	blur?: BlurBgRes;
	replace?: ReplaceBgRes;
};

type RemoveBgRes = {
	result_url: string;
};

type BlurBgRes = {
	result_url: string;
	sid: string;
};

type ReplaceBgRes = {
	result_url: string;
	seed: string;
	sid: string;
};

export type OfflineProps = {
	prefix_id: string;
	variation: number;
	original_size?: {
		width?: number;
		height?: number;
	};
};

export type ReplaceBgConfig = {
	bg_prompt?: string;
	refine_prompt?: boolean;
};

export type ConfigProps = {
	title: string;
	checked?: boolean;
	onClick?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	isActive?: boolean;
	setIsActive?: (config: string) => void;
	titleOnly?: boolean;
	className?: string;
	tooltip?: string;
	children?: React.ReactNode;
};

export type BackgroundConfigType = {
	remove?: boolean;
	replace?: ReplaceBgConfig;
	expand?: boolean;
	solid?: { selected?: boolean; color_code?: string };
	generateByText?: {
		selected?: boolean;
		model_version?: string;
		prompt?: string;
		num_results?: number;
		fast_version?: boolean;
		binarize_alpha?: boolean;
		refine_prompt?: boolean;
	};
	generateByImage?: {
		selected?: boolean;
		ref_image_url?: string;
		num_results?: number;
	};
	generatedBackgrounds?: {
		selected?: boolean;
		image?: PlaygroundImage;
	};
	original?: boolean;
	is_fox?: boolean;
	blur?: boolean;
};

export type ForegroundPlacementType = {
	placements?: {
		selected?: boolean;
		values?: string[];
	};
};

export type SizeConfigType = {
	crop?: boolean;
	aspect_ratio?: {
		selected?: boolean;
		value?: string[];
	};
	custom_size?: {
		selected?: boolean;
		width?: number;
		height?: number;
	};
	medium_destination?: {
		selected?: boolean;
		platform?: Platform;
		destinations?: PlatformDestination[];
	};
	increase_resolution?: {
		selected?: boolean;
		value?: string[];
	};
};

export type croppedAreaPixelsType = {
	x: number;
	y: number;
	width: number;
	height: number;
	newWidth?: number;
	newHeight?: number;
};

export type ManualPlacementConfigType = {
	width?: number;
	height?: number;
	canvas_size?: [number, number];
	original_image_size?: [number, number];
	original_image_location?: [number, number];
	cropped_area_pixels?: croppedAreaPixelsType;
	cropped_area?: any;
};

export type ResponsePlatformDestination = {
	name: string;
	width: number;
	height: number;
	aspectRatioHeight: number;
	aspectRatioWidth: number;
	index: number;
	resize: { width: number; height: number };
};

export type ResponsePlatform = {
	key: string;
	value?: ResponsePlatformDestination[];
};

export type PlatformDestination = {
	name: string;
	width: number;
	height: number;
};

export type Platform = {
	key: string;
	value?: PlatformDestination[];
	get?: (platform: string) => ResponsePlatformDestination[] | undefined;
};

export type ConfigValue = boolean | string | number | object | File;

export interface ConfigDisplayProps {
	config: any;
	onDelete: (configKey: string, value: any) => void;
	onClick?: () => void;
	expandTo?: ReactNode;
	subMenuPanelProps?: Record<string, any>;
}

export interface ConfigItemProps {
	configKey: string;
	onDelete: (configKey: string, value: any) => void;
	label?: string;
	useExactLetterCase?: boolean;
	value: any;
	onClick?: () => void;
	children?: React.ReactNode;
}

export enum ConfigKeys {
	Selected = "selected",
	Prompt = "prompt",
	Solid = "solid",
	GenerateByText = "generateByText",
	GenerateByImage = "generateByImage",
	AspectRatio = "aspect_ratio",
	CustomSize = "custom_size",
	MediumDestination = "medium_destination",
	Placements = "placements",
	IncreaseResolution = "increase_resolution",
}

export type SelectedConfigsProps = {
	background?: string[];
	size?: string[];
	foreground_placement?: string[];
	image_reference?: string[];
};

export type ObjectManipulationType = {
	selected?: boolean;
	prompt?: string;
	num_results?: number;
};

export interface ImageEditingConfigsProps {
	handleGenerateImageToImage: () => void;
}

export interface ImageEditingConfigComponentProps {
	onBackButtonClick?: () => void;
}
